import { api, setAccessToken, setRefreshToken, getRefreshToken } from './client';
import { isElectron } from '../platform/external';

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: { id: string; email: string; displayName: string; role: string };
}

/**
 * Hosted-mode signup may return 202 with `pendingVerification: true` rather
 * than tokens - the renderer should show a "check your email" screen instead
 * of signing the user in. The response is discriminated so callers can branch
 * cleanly without inspecting numbers.
 */
export type SignupResponse =
  | (LoginResponse & { pendingVerification?: false })
  | {
      pendingVerification: true;
      message: string;
      user: { id: string; email: string; displayName: string; role: string };
    };

const REMEMBERED_EMAIL_KEY = 'tccrm.rememberedEmail';

function persistTokens(res: LoginResponse, rememberMe: boolean, email: string): void {
  setAccessToken(res.accessToken);
  setRefreshToken(res.refreshToken);
  try {
    if (rememberMe) localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
    else localStorage.removeItem(REMEMBERED_EMAIL_KEY);
  } catch {
    /* ignore */
  }
}

export async function login(
  email: string,
  password: string,
  rememberMe = false,
): Promise<LoginResponse> {
  const res = await api<LoginResponse>('/auth/login', {
    method: 'POST',
    body: { email, password, rememberMe },
  });
  persistTokens(res, rememberMe, email);
  return res;
}

/**
 * Self-signup. Creates an account on the @tricorian.com allowlist and
 * immediately signs the user in by storing the access + refresh tokens.
 */
export async function signup(args: {
  email: string;
  displayName: string;
  password: string;
  rememberMe?: boolean;
}): Promise<SignupResponse> {
  const rememberMe = args.rememberMe ?? false;
  const res = await api<SignupResponse>('/auth/signup', {
    method: 'POST',
    body: {
      email: args.email,
      displayName: args.displayName,
      password: args.password,
      rememberMe,
    },
  });
  if ('accessToken' in res) {
    persistTokens(res, rememberMe, args.email);
  }
  return res;
}

/**
 * Request a password-reset email. The server always returns 200 regardless of
 * whether the email matched a user, so the caller cannot use the response to
 * probe account existence. Use the side-effect of the email arriving.
 */
export async function forgotPassword(email: string): Promise<void> {
  await api<{ ok: true }>('/auth/forgot', {
    method: 'POST',
    body: { email },
  });
}

/** Complete a password reset using the token from the email link. */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await api<{ ok: true }>('/auth/reset', {
    method: 'POST',
    body: { token, newPassword },
  });
}

/** Re-send the email-verification link. Same enumeration-proof 200 contract. */
export async function resendVerificationEmail(email: string): Promise<void> {
  await api<{ ok: true }>('/auth/verify-email/resend', {
    method: 'POST',
    body: { email },
  });
}

/**
 * Complete the browser-based Microsoft SSO flow by trading the one-time
 * exchange code (returned to the SPA via /login/sso-complete?exchange=...)
 * for the access + refresh token pair.
 */
export async function completeMicrosoftWebSso(
  exchangeCode: string,
): Promise<LoginResponse> {
  const res = await api<LoginResponse>('/auth/microsoft/web/exchange', {
    method: 'POST',
    body: { exchangeCode },
  });
  // SSO sessions default to "remember me" since the user just went through a
  // full browser auth; they expect to stay signed in.
  persistTokens(res, true, res.user.email);
  return res;
}

export function getRememberedEmail(): string | null {
  try {
    return localStorage.getItem(REMEMBERED_EMAIL_KEY);
  } catch {
    return null;
  }
}

/**
 * Start the Microsoft SSO flow.
 *
 * In Electron the preload exposes `signInWithMicrosoft()` which performs the
 * authorisation-code-with-PKCE dance in the system browser and returns an
 * id_token, which we POST to /auth/microsoft/callback. The renderer never
 * leaves the BrowserWindow.
 *
 * In the hosted web app there is no preload, so we hard-redirect the tab to
 * /api/auth/microsoft/web/start. The server hands the user off to Microsoft,
 * receives the callback, and redirects back to /login/sso-complete?exchange=…
 * where SsoCompletePage picks up the exchange code and finishes the flow.
 * That branch never resolves the promise (page is navigating away).
 */
export async function loginWithMicrosoft(rememberMe = true): Promise<LoginResponse> {
  if (isElectron()) {
    const w = window as unknown as {
      tcCrm?: { signInWithMicrosoft?: () => Promise<{ idToken: string }> };
    };
    const fn = w.tcCrm?.signInWithMicrosoft;
    if (typeof fn !== 'function') {
      throw new Error('Microsoft SSO is unavailable in this build.');
    }
    const { idToken } = await fn();
    const res = await api<LoginResponse>('/auth/microsoft/callback', {
      method: 'POST',
      body: { idToken, rememberMe },
    });
    persistTokens(res, rememberMe, res.user.email);
    return res;
  }

  // Web: redirect to the server's authorize endpoint. The server signs the
  // PKCE state, redirects to Microsoft, then back to /api/auth/microsoft/web/callback,
  // and finally bounces the SPA to /login/sso-complete with the exchange code.
  // The renderer's main process / hosted CSP must permit this navigation.
  const returnTo = window.location.pathname + window.location.search;
  const start = '/api/auth/microsoft/web/start?returnTo=' + encodeURIComponent(returnTo);
  window.location.assign(start);
  // Page is navigating; the promise here is intentionally never resolved.
  return new Promise<LoginResponse>(() => {});
}

export async function microsoftSsoConfigured(): Promise<boolean> {
  try {
    const res = await api<{ configured: boolean }>('/auth/microsoft/status');
    return res.configured;
  } catch {
    return false;
  }
}

/**
 * Server-side logout - revokes the refresh token (and every other live
 * refresh row for this user) so a leaked access token is moot once it
 * naturally expires. Always clears local tokens, even if the server call
 * fails (the user wants out; we let them out).
 */
export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken() ?? undefined;
  try {
    await api<void>('/auth/logout', {
      method: 'POST',
      body: { refreshToken },
    });
  } catch {
    /* network or 4xx - proceed with local cleanup anyway */
  }
  setAccessToken(null);
  setRefreshToken(null);
}
